import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Car, Bike, BookmarkPlus, Check } from 'lucide-react';
import { inputCls, notifyErr } from './shared';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

const TYPE_OPTIONS = [
  { value: 'car', label: 'Car', icon: Car },
  { value: 'motorcycle', label: 'Motorcycle', icon: Bike },
  { value: 'scooter', label: 'Scooter', icon: Bike },
];

const CATALOG_KEY = { car: 'cars', motorcycle: 'motorcycles', scooter: 'scooters' };

const emptyVehicle = () => ({
  id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID().replace(/-/g, '') : `v-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type: 'car',
  make: '',
  model: '',
  registration_number: '',
});

/**
 * Compact editor for a customer's linked vehicles. Uses native <datalist>
 * for autocompletion against the curated catalog so typing is fast and
 * works on iPad Safari without any extra dependencies.
 */
const VehicleEditor = ({ vehicles = [], onChange, catalog = null, onCatalogChange = null }) => {
  const { authHeader } = useAuth();
  const datalistId = useMemo(() => `vehicle-makes-${Math.random().toString(36).slice(2, 8)}`, []);
  // Track which rows have just been saved to the catalog (per-row check icon)
  const [savedRows, setSavedRows] = useState({});
  const [savingRows, setSavingRows] = useState({});

  // Build a flat make → models lookup so the model datalist updates when the
  // user picks a make from a different brand bucket.
  const lookups = useMemo(() => {
    if (!catalog) return { makesByType: { car: [], motorcycle: [], scooter: [] }, modelsByMake: {} };
    const makesByType = {};
    const modelsByMake = {};
    Object.entries(CATALOG_KEY).forEach(([type, key]) => {
      const brands = catalog[key] || {};
      makesByType[type] = Object.keys(brands).sort();
      Object.entries(brands).forEach(([make, models]) => {
        // Same make can appear under multiple categories (e.g. Honda) — merge.
        modelsByMake[make] = Array.from(new Set([...(modelsByMake[make] || []), ...(models || [])]));
      });
    });
    return { makesByType, modelsByMake };
  }, [catalog]);

  const update = (idx, patch) => {
    const next = vehicles.map((v, i) => (i === idx ? { ...v, ...patch } : v));
    onChange(next);
  };

  const remove = (idx) => onChange(vehicles.filter((_, i) => i !== idx));

  const add = () => onChange([...vehicles, emptyVehicle()]);

  // True when the make/model in this row isn't already part of the loaded
  // catalog — so the "Save to catalog" affordance can show.
  const isCustomEntry = (v) => {
    if (!v.make || !v.model) return false;
    const modelsForMake = lookups.modelsByMake[v.make] || [];
    return !modelsForMake.includes(v.model);
  };

  const saveToCatalog = async (idx, v) => {
    if (!v.make || !v.model) return;
    setSavingRows((prev) => ({ ...prev, [idx]: true }));
    try {
      await axios.post(
        `${API}/billing/vehicles/catalog/entry`,
        { type: v.type, make: v.make.trim(), model: v.model.trim() },
        { headers: authHeader() },
      );
      setSavedRows((prev) => ({ ...prev, [idx]: true }));
      if (onCatalogChange) onCatalogChange();
    } catch (e) {
      notifyErr(e);
    } finally {
      setSavingRows((prev) => ({ ...prev, [idx]: false }));
    }
  };

  return (
    <div className="space-y-3" data-testid="vehicle-editor">
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500">Vehicles</label>
          <p className="text-[11px] text-slate-400 mt-0.5">Type any make/model — the dropdown shows suggestions, but you can enter anything not in the list.</p>
        </div>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 hover:text-teal-700 shrink-0"
          data-testid="add-vehicle-btn"
        >
          <Plus size={14} /> Add vehicle
        </button>
      </div>

      {vehicles.length === 0 && (
        <p className="text-xs text-slate-400 italic">No vehicles linked. Click <b>+ Add vehicle</b> to link one.</p>
      )}

      {vehicles.map((v, idx) => {
        const makesList = lookups.makesByType[v.type] || [];
        const modelsList = lookups.modelsByMake[v.make] || [];
        const makesId = `${datalistId}-makes-${v.type}`;
        const modelsId = `${datalistId}-models-${idx}`;
        const showSaveBtn = isCustomEntry(v) && !savedRows[idx];
        return (
          <div
            key={v.id || idx}
            className="grid grid-cols-12 gap-2 items-start bg-slate-50 border border-slate-200 rounded-lg p-2"
            data-testid={`vehicle-row-${idx}`}
          >
            <div className="col-span-12 sm:col-span-2">
              <select
                value={v.type}
                onChange={(e) => update(idx, { type: e.target.value })}
                className={inputCls}
                data-testid={`vehicle-type-${idx}`}
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <input
                list={makesId}
                value={v.make}
                onChange={(e) => {
                  update(idx, { make: e.target.value, model: '' });
                  setSavedRows((p) => ({ ...p, [idx]: false }));
                }}
                placeholder="Make"
                className={inputCls}
                data-testid={`vehicle-make-${idx}`}
              />
              <datalist id={makesId}>
                {makesList.map((m) => <option key={m} value={m} />)}
              </datalist>
            </div>
            <div className="col-span-6 sm:col-span-3">
              <div className="relative">
                <input
                  list={modelsId}
                  value={v.model}
                  onChange={(e) => {
                    update(idx, { model: e.target.value });
                    setSavedRows((p) => ({ ...p, [idx]: false }));
                  }}
                  placeholder="Model"
                  className={`${inputCls} ${showSaveBtn ? 'pr-9' : ''}`}
                  data-testid={`vehicle-model-${idx}`}
                />
                <datalist id={modelsId}>
                  {modelsList.map((m) => <option key={m} value={m} />)}
                </datalist>
                {showSaveBtn && (
                  <button
                    type="button"
                    onClick={() => saveToCatalog(idx, v)}
                    disabled={savingRows[idx]}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded disabled:opacity-50"
                    title="Save this make/model to the catalog so it shows up next time"
                    data-testid={`vehicle-save-catalog-${idx}`}
                  >
                    <BookmarkPlus size={14} />
                  </button>
                )}
                {savedRows[idx] && (
                  <span
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-emerald-600"
                    title="Added to catalog"
                    data-testid={`vehicle-saved-catalog-${idx}`}
                  >
                    <Check size={14} />
                  </span>
                )}
              </div>
            </div>
            <div className="col-span-10 sm:col-span-3">
              <input
                value={v.registration_number || ''}
                onChange={(e) => update(idx, { registration_number: e.target.value.toUpperCase() })}
                placeholder="Reg. No. (KA-01-AB-1234)"
                className={inputCls}
                data-testid={`vehicle-reg-${idx}`}
                maxLength={20}
              />
            </div>
            <div className="col-span-2 sm:col-span-1 flex justify-end">
              <button
                type="button"
                onClick={() => remove(idx)}
                className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                title="Remove vehicle"
                data-testid={`vehicle-remove-${idx}`}
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VehicleEditor;
